import { useEffect, useRef, useState } from 'react'
import { type CustomCellEditorProps } from 'ag-grid-react'
import type { TableRow } from './types'

// AG Grid v35 custom editor — новый API: CustomCellEditorProps + onValueChange
export function TextareaEditor({
  value,
  onValueChange,
  eventKey,
}: CustomCellEditorProps<TableRow, string>) {
  const startValue = eventKey && eventKey.length === 1 ? eventKey : String(value ?? '')
  const [localValue, setLocalValue] = useState<string>(startValue)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Авто-высота редактора: растёт под введённый текст прямо во время набора,
  // чтобы длинное значение было видно целиком (не скроллилось в одну строку).
  // Работает вместе с cellEditorPopup:true у колонки — попап не зажат ячейкой.
  const autosize = () => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 34), 320)}px`
  }

  useEffect(() => {
    // Если редактирование началось с нажатия клавиши — сообщаем грид сразу
    if (eventKey && eventKey.length === 1) {
      onValueChange(eventKey)
    }
    const el = textareaRef.current
    if (!el) return
    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
    autosize()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- намеренно: фокус и стартовый ввод только при маунте редактора, eventKey/onValueChange здесь стабильны на время жизни компонента
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value)
    onValueChange(e.target.value)
    autosize()
  }

  return (
    <textarea
      ref={textareaRef}
      value={localValue}
      onChange={handleChange}
      onKeyDown={(e) => {
        // Cmd/Ctrl+Enter, Alt/Option+Enter, Shift+Enter — вставить перенос
        // строки внутри ячейки (Excel-paradigm: Option+Enter на Mac, Alt+Enter
        // на Windows). Просто Enter без модификаторов — стандартное поведение
        // AG Grid (commit ячейки).
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey)) {
          e.preventDefault()
          e.stopPropagation()
          const el = e.currentTarget
          const pos = el.selectionStart ?? localValue.length
          const newVal = localValue.slice(0, pos) + '\n' + localValue.slice(pos)
          setLocalValue(newVal)
          onValueChange(newVal)
          setTimeout(() => {
            el.setSelectionRange(pos + 1, pos + 1)
            autosize()
          }, 0)
          return
        }
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
          e.preventDefault()
        }
      }}
      style={{
        width: '100%',
        height: 'auto',
        minHeight: 34,
        maxHeight: 320,
        overflowY: 'auto',
        border: 'none',
        padding: '3px 7px',
        resize: 'none',
        fontSize: 13,
        lineHeight: 1.4,
        fontFamily: 'inherit',
        outline: 'none',
        background: '#fff',
        boxSizing: 'border-box',
      }}
    />
  )
}
TextareaEditor.displayName = 'TextareaEditor'
